import React, { useEffect, useState, useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { useTranslation } from 'react-i18next';

// Hook personalizado para el count-up
const useCountUp = (end: number, duration: number = 2000, startOnView: boolean = true) => {
  const [count, setCount] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  useEffect(() => {
    if (!startOnView || !isInView) return;

    let startTime: number;
    let animationId: number;

    const animate = (currentTime: number) => {
      if (!startTime) startTime = currentTime;
      const progress = Math.min((currentTime - startTime) / duration, 1);
      
      // Easing function (ease-out)
      const easeOut = 1 - Math.pow(1 - progress, 3);
      const currentCount = Math.floor(easeOut * end);
      
      setCount(currentCount);
      
      if (progress < 1) {
        animationId = requestAnimationFrame(animate);
      } else {
        setCount(end);
        setIsAnimating(false);
      }
    };

    setIsAnimating(true);
    animationId = requestAnimationFrame(animate);

    return () => {
      if (animationId) {
        cancelAnimationFrame(animationId);
      }
    };
  }, [end, duration, isInView, startOnView]);

  return { count, isAnimating, ref };
};

// Datos de impacto
const getImpacts = (t: (key: string) => string) => [
  {
    id: 'deportistas',
    number: 100000,
    prefix: '+',
    suffix: '',
    description: t('impact.athletes')
  },
  {
    id: 'sesiones',
    number: 7,
    prefix: '+',
    suffix: 'M',
    description: t('impact.sessions')
  },
  {
    id: 'paises',
    number: 40,
    prefix: '+',
    suffix: '',
    description: t('impact.countries')
  },
  {
    id: 'aplicaciones',
    number: 30,
    prefix: '+',
    suffix: '',
    description: t('impact.applications')
  },
  {
    id: 'especializacion',
    number: 8,
    prefix: '',
    suffix: ` ${t('impact.years')}`,
    description: t('impact.specialization')
  },
  {
    id: 'experiencia',
    number: 20,
    prefix: '',
    suffix: ` ${t('impact.years')}`,
    description: t('impact.experience')
  }
];

// Componente individual para cada hito
const ImpactCard: React.FC<{ impact: ReturnType<typeof getImpacts>[0] }> = ({ impact }) => {
  const { count, ref } = useCountUp(impact.number, 2000);

  return (
    <motion.div
      ref={ref}
      className="rounded-2xl bg-white shadow-lg ring-1 ring-black/5 p-6 md:p-8 transition-transform hover:scale-[1.01] hover:shadow-xl"
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, ease: "easeOut" }}
      viewport={{ once: true }}
    >
      <p 
        role="text" 
        className="text-4xl md:text-5xl font-bold leading-none"
        style={{ color: 'var(--stpp-green, #00A86B)' }}
        aria-label={`${impact.prefix}${count}${impact.suffix}`}
      >
        {impact.prefix}{count.toLocaleString()}{impact.suffix}
      </p>
      <p className="mt-2 text-sm md:text-base text-neutral-600">
        {impact.description}
      </p>
    </motion.div>
  );
};

// Componente principal
const ImpactSection: React.FC = () => {
  const { t } = useTranslation();
  const impacts = getImpacts(t);

  return (
    <section 
      className="py-16 md:py-24"
      style={{ backgroundColor: 'var(--stpp-surface, #F7F7F8)' }}
      aria-labelledby="impacto-heading"
    >
      <div className="mx-auto max-w-screen-2xl px-4 md:px-6">
        <motion.h2
          id="impacto-heading"
          className="text-3xl md:text-5xl font-semibold tracking-tight text-center"
          style={{ color: 'var(--stpp-black, #0B0F12)' }}
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: "easeOut" }}
          viewport={{ once: true }}
        >
          {t('impact.title')}
        </motion.h2>

        <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
          {impacts.map((impact) => (
            <ImpactCard key={impact.id} impact={impact} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default ImpactSection;
